import {
  useEffect, useCallback, useState, useMemo,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AuthContext, type AuthContextType } from "@/features/auth/context/AuthContext";
import { useAuthMutations } from "@/features/auth/hooks/useAuthMutations";
import { useAuthState } from "@/features/auth/hooks/useAuthState";
import { useOrganizationQuery } from "@/features/auth/hooks/useOrganizationQuery";
import { usePlanQuery } from "@/features/auth/hooks/usePlanQuery";
import { useUserQuery } from "@/features/auth/hooks/useUserQuery";
import type { LoginFormData } from "@/features/auth/schemas/forms/loginForm.schema";
import * as authService from "@/features/auth/services/auth.service";
import { authStorageService } from "@/features/auth/services/storage.service";
import {
  type AuthCheck2FAPayload,
  type AuthCheck2FAResponse,
  type AuthErrorResponse,
  type AuthRegisterPhonePayload,
  type ImpersonatePayload,
  isTwoFactorRequired,
} from "@/features/auth/types/auth.type";
import { extractAccessToken, markLegalComplianceAcceptedOrNull } from "@/features/auth/utils/auth.utils";
import type {
  PusherPresenceEvent,
  PusherMemberAddedEvent,
  PusherMemberRemovedEvent,
} from "@/features/chat/types";
import { getUserPresenceId } from "@/features/chat/utils/getUserPresenceId";
import { useCmsPagesQuery } from "@/features/shop/cms/hooks/useCmsPagesQuery";
import { setUser } from "@/shared/clients/sentry.client";
import { usePush } from "@/shared/hooks/usePush";
import { authSessionBus } from "@/shared/lib/authSessionBus";
import type { User, UserResponse } from "@/shared/types/";

type AuthProviderProps = { children: React.ReactNode };

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [onlineUserIds, setOnlineUserIds] = useState<Set<string>>(new Set());
  const { subscribe, unsubscribe } = usePush();
  // The nearest QueryClientProvider — the AppDependencies singleton in
  // production, a test-local client in tests — not the `@/shared/lib/query-client`
  // import: useUserQuery observes the same cache instance to avoid a stale flag.
  const queryClient = useQueryClient();

  const {
    session,
    onGetMeSuccess,
    onLoginSuccess,
    onCheck2FASuccess,
    onTwoFactorRequired,
    onError,
    onRefreshSessionSuccess,
    onRefreshSessionFailed,
    clearError,
    onLoggedOut,
    onAuthenticating,
    onPlanSuccess,
    onOrganizationSuccess,
    onUserOrPlanError,
    updateUserState,
    onCmsPagesSuccess,
    onCmsPagesError,
    onLegalComplianceCompleted,
  } = useAuthState();

  const {
    loginMutation,
    check2FAMutation,
    registerPhoneMutation,
    refreshSessionMutation,
    logoutMutation,
    impersonateMutation,
    exchangeGrantMutation,
  } = useAuthMutations({
    onLoginSuccess,
    onCheck2FASuccess,
    onRefreshSessionSuccess,
    onRefreshSessionFailed,
    onError,
  });

  const login = useCallback(async (credentials: LoginFormData) => {
    await loginMutation.mutateAsync(credentials);
  }, [loginMutation]);

  const refreshSession = useCallback(async (): Promise<void> => {
    await refreshSessionMutation.mutateAsync();
  }, [refreshSessionMutation]);

  const check2FA = useCallback(async (payload: Omit<AuthCheck2FAPayload, "otp_challenge">) => {
    await check2FAMutation.mutateAsync({
      username: payload.username,
      password: payload.password,
      otp_challenge: session.twoFactorData.otp_challenge ?? "",
      otp_code: payload.otp_code,
    });
  }, [check2FAMutation]);

  const pollLoginApproval = useCallback(async (): Promise<boolean> => {
    const { otp_challenge: challenge, approval } = session.twoFactorData;
    if (!challenge || !approval) return false;

    const data = await authService.pollLoginApproval({ challenge, pollSecret: approval.pollSecret });
    if ("status" in data && data.status === "pending") return true;

    // A token signs in as after a typed code; an error (denied, expired) ends the wait.
    onCheck2FASuccess(data as AuthCheck2FAResponse);
    return false;
  }, [session.twoFactorData, onCheck2FASuccess]);

  const fallbackLoginApprovalToSms = useCallback(async (): Promise<void> => {
    const { otp_challenge: challenge, approval } = session.twoFactorData;
    if (!challenge || !approval) return;

    clearError();
    const data = await authService.fallbackLoginApprovalToSms({ challenge, pollSecret: approval.pollSecret });
    if (isTwoFactorRequired(data)) return onTwoFactorRequired(data);
    onError(data as AuthErrorResponse);
  }, [session.twoFactorData, onTwoFactorRequired, onError, clearError]);

  const registerPhone = useCallback(async (payload: AuthRegisterPhonePayload) => {
    await registerPhoneMutation.mutateAsync(payload);
  }, [registerPhoneMutation]);

  const impersonate = useCallback(async (payload: ImpersonatePayload) => {
    await impersonateMutation.mutateAsync(payload);
  }, [impersonateMutation]);

  const exchangeGrant = useCallback(async (loginToken: string) => {
    await exchangeGrantMutation.mutateAsync(loginToken);
  }, [exchangeGrantMutation]);

  const completeLegalCompliance = useCallback((employeeUpdates: Partial<User> = {}) => {
    // Patch the query cache immediately: useUserQuery replays every
    // `["user"]` cache write into onGetMeSuccess, and a stale flag there
    // would re-open the step on the very next write during the chain.
    queryClient.setQueryData<UserResponse>(["user"], (user) => markLegalComplianceAcceptedOrNull(user, employeeUpdates));
    onLegalComplianceCompleted(employeeUpdates);
  }, [queryClient, onLegalComplianceCompleted]);

  const logout = useCallback(async () => {
    onLoggedOut();

    // Reset all auth mutations to clear stale .data (prevents stale token extraction)
    loginMutation.reset();
    check2FAMutation.reset();
    refreshSessionMutation.reset();
    impersonateMutation.reset();
    exchangeGrantMutation.reset();
    registerPhoneMutation.reset();

    await logoutMutation.mutateAsync();
  }, [
    logoutMutation,
    loginMutation,
    check2FAMutation,
    refreshSessionMutation,
    impersonateMutation,
    exchangeGrantMutation,
    registerPhoneMutation,
    onLoggedOut,
  ]);

  const accessToken =
    extractAccessToken(loginMutation.data) ??
    extractAccessToken(check2FAMutation.data) ??
    extractAccessToken(refreshSessionMutation.data) ??
    extractAccessToken(impersonateMutation.data) ??
    extractAccessToken(exchangeGrantMutation.data) ??
    authStorageService.getAuthToken();

  const onGetMeError = useCallback(() => {
    logout();
    onUserOrPlanError();
  }, [onUserOrPlanError]);

  useUserQuery({
    enabled: session.isWaitingForUser,
    onGetMeSuccess,
    onGetMeError,
  });

  // Fetch organization after successful user data fetch
  const organizationId = session.user?.employee?.organization_id ?? null;
  useOrganizationQuery({
    enabled: session.isWaitingForOrganization && !!organizationId,
    organizationId,
    accessToken,
    onGetOrganizationSuccess: onOrganizationSuccess,
    onGetOrganizationError: () => {
      logout();
      onUserOrPlanError();
    },
  });

  // Fetch plan after successful organization data fetch
  usePlanQuery({
    enabled: session.isWaitingForPlan && !!organizationId,
    organizationId,
    accessToken,
    onGetPlanSuccess: onPlanSuccess,
    onGetPlanError: () => {
      logout();
      onUserOrPlanError();
    },
  });

  // Fetch CMS pages after successful plan data fetch
  useCmsPagesQuery({
    enabled: session.isWaitingForCmsPages,
    accessToken,
    onGetCmsPagesSuccess: onCmsPagesSuccess,
    onGetCmsPagesError: onCmsPagesError,
  });

  useEffect(() => {
    const isMutating = loginMutation.isPending || check2FAMutation.isPending || impersonateMutation.isPending || exchangeGrantMutation.isPending || refreshSessionMutation.isPending;
    const isWaitingForData = session.isWaitingForUser || session.isWaitingForOrganization || session.isWaitingForPlan || session.isWaitingForCmsPages;
    onAuthenticating(isMutating || isWaitingForData);
  }, [
    loginMutation.isPending,
    check2FAMutation.isPending,
    impersonateMutation.isPending,
    exchangeGrantMutation.isPending,
    refreshSessionMutation.isPending,
    session.isWaitingForUser,
    session.isWaitingForOrganization,
    session.isWaitingForPlan,
    session.isWaitingForCmsPages,
  ]);

  useEffect(() => {
    authSessionBus.setUnauthorizedHandler(async () => {
      logout();
    });
    return () => authSessionBus.setUnauthorizedHandler(null);
  }, [logout]);

  // New CGU published mid-session: the API answers 403 on every call. Only an
  // authenticated session is logged out — during the login step the 403 is
  // expected and the acceptance form is already displayed. Concurrent 403s
  // share the same closure, so logout runs once per authenticated session.
  useEffect(() => {
    if (!session.isAuthenticated) return;

    let hasLoggedOut = false;
    authSessionBus.setLegalAcceptanceRequiredHandler(() => {
      if (hasLoggedOut) return;
      hasLoggedOut = true;
      logout();
    });
    return () => authSessionBus.setLegalAcceptanceRequiredHandler(null);
  }, [session.isAuthenticated, logout]);

  const isUserOnline = (userId: number): boolean => {
    return onlineUserIds.has(String(userId));
  };

  useEffect(() => {
    if (!session.isAuthenticated || !session.user?.employee?.key_chat) {
      return;
    }

    const organizationId = getUserPresenceId(session.user.employee.key_chat);

    if (!organizationId) {
      return;
    }

    const channelName = `presence-entreprise-${organizationId}`;

    const subscriptionSucceededHandler = (data: unknown) => {
      // Handle Pusher Members object format
      if (data && typeof data === "object" && "members" in data) {
        const membersData = data as { members: Record<string, unknown>, count: number, myID: number };
        const memberIds = Object.keys(membersData.members);
        setOnlineUserIds(new Set(memberIds));
      }
      // Handle standard presence event format
      else if (data && typeof data === "object" && "presence" in data) {
        const presenceData = data as PusherPresenceEvent;
        setOnlineUserIds(new Set(presenceData.presence.ids));
      }
    };

    const memberAddedHandler = (data: PusherMemberAddedEvent) => {
      setOnlineUserIds((prev) => new Set(prev).add(String(data.id)));
    };

    const memberRemovedHandler = (data: PusherMemberRemovedEvent) => {
      setOnlineUserIds((prev) => {
        const updated = new Set(prev);
        updated.delete(String(data.id));
        return updated;
      });
    };

    subscribe(channelName, "pusher:subscription_succeeded", subscriptionSucceededHandler);
    subscribe<PusherMemberAddedEvent>(channelName, "pusher:member_added", memberAddedHandler);
    subscribe<PusherMemberRemovedEvent>(channelName, "pusher:member_removed", memberRemovedHandler);

    return () => {
      unsubscribe(channelName);
    };
  }, [session.isAuthenticated, session.user?.employee?.key_chat, subscribe, unsubscribe]);

  useEffect(() => {
    if (session.isAuthenticated && session.user?.employee) {
      const { id, organization_id } = session.user.employee;
      setUser({
        id,
        organization_id,
      });
    }
    else {
      setUser(null);
    }
  }, [session.isAuthenticated, session.user?.employee?.id, session.user?.employee?.organization_id]);

  const value: AuthContextType = useMemo(() => ({
    ...session,
    login,
    impersonate,
    exchangeGrant,
    check2FA,
    pollLoginApproval,
    fallbackLoginApprovalToSms,
    registerPhone,
    refreshSession,
    logout,
    clearError,
    updateUserState,
    completeLegalCompliance,
    onlineUserIds,
    isUserOnline,
  }), [
    session,
    login,
    impersonate,
    exchangeGrant,
    check2FA,
    registerPhone,
    refreshSession,
    logout,
    clearError,
    updateUserState,
    completeLegalCompliance,
    onlineUserIds,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
